import { BRIEF_THEMES_SHOWN, type BriefCard } from '@sigora/domain';
import { useId, useState } from 'react';

import {
  THEMES_FEWER_LABEL,
  THEMES_FEWER_NAME,
  themeFootnote,
  themeLine,
  themesMoreLabel,
  themesMoreName,
} from '../brief-copy.ts';

type ThemesVisual = Extract<NonNullable<BriefCard['visual']>, { kind: 'themes' }>;

/**
 * The themes a research question's answers fell into, most raised first, each with how many of the
 * coded people it covered. The first `BRIEF_THEMES_SHOWN` show; the rest wait behind "+K more",
 * which opens them in place and closes them again. A row whose label another row repeats names the
 * guide question it was coded on. Under every strip, the footnote says each person counts once in
 * a theme — a reader adding the counts up would otherwise read them as answers — how many people
 * were coded, and, when the stored visual says the floor left any theme out, how many and the floor.
 *
 * The rows behind the toggle are in the page while hidden, so its `aria-controls` always names the
 * list. With `allRows` — a page nobody can press, such as the printed Brief — every row is drawn
 * and there is no toggle.
 */
export const ThemeStrip = ({
  visual,
  allRows = false,
}: {
  visual: ThemesVisual;
  allRows?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const coded = visual.coded_participants;
  const shown = allRows ? visual.themes.length : Math.min(BRIEF_THEMES_SHOWN, visual.themes.length);
  const more = visual.themes.length - shown;
  const leftOut =
    visual.floor !== undefined && visual.left_out !== undefined && visual.left_out > 0
      ? { count: visual.left_out, floor: visual.floor }
      : undefined;

  return (
    <figure className="brief-themes" data-test-id="brief-themes">
      <ul className="brief-bars" id={listId}>
        {visual.themes.map((theme, index) => (
          <li key={theme.theme_id} data-test-id="brief-theme" hidden={index >= shown && !open}>
            <span className="brief-bar-label">{theme.label}</span>
            {theme.source !== undefined && (
              <span className="brief-meta brief-theme-source" data-test-id="brief-theme-source">
                {theme.source}
              </span>
            )}
            <span className="brief-bar-track" aria-hidden="true">
              <span
                className="brief-bar"
                style={{
                  width: `${String(coded === 0 ? 0 : (theme.participants / coded) * 100)}%`,
                }}
              />
            </span>
            <span className="brief-meta" data-test-id="brief-theme-line">
              {themeLine(theme.participants, coded)}
            </span>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <button
          type="button"
          className="brief-theme-more"
          data-test-id="brief-theme-more"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={open ? THEMES_FEWER_NAME : themesMoreName(more)}
          onClick={() => setOpen((opened) => !opened)}
        >
          {open ? THEMES_FEWER_LABEL : themesMoreLabel(more)}
        </button>
      )}
      <figcaption className="brief-meta" data-test-id="brief-theme-footnote">
        {themeFootnote(coded, leftOut)}
      </figcaption>
    </figure>
  );
};
